import { useMemo, useState } from "react";
import { toast } from "react-toastify";
import NewServiceTierRow from "./NewServiceTierRow";

const AddServiceModal = ({ open, onClose, onCreated }) => {
    const initial = useMemo(
        () => ({
            serviceName: "",
            serviceCode: "",
            price: "",
            additionalFee: "",
            priceTiers: [{ minQty: 1, maxQty: 1, price: "" }],
        }),
        [],
    );

    const [form, setForm] = useState(initial);

    if (!open) return null;

    const reset = () => setForm(initial);

    const addTier = () => {
        const last = form.priceTiers[form.priceTiers.length - 1];
        const newMinQty = last.maxQty ? Number(last.maxQty) + 1 : 2;

        setForm((prev) => ({
            ...prev,
            priceTiers: [...prev.priceTiers, { minQty: newMinQty, maxQty: null, price: "" }],
        }));
    };

    const removeTier = (index) => {
        if (form.priceTiers.length <= 1) {
            toast.error("Phải có ít nhất 1 tier giá");
            return;
        }
        setForm((prev) => ({
            ...prev,
            priceTiers: prev.priceTiers.filter((_, i) => i !== index),
        }));
    };

    const updateTier = (index, patch) => {
        setForm((prev) => {
            const tiers = [...prev.priceTiers];
            tiers[index] = { ...tiers[index], ...patch };
            return { ...prev, priceTiers: tiers };
        });
    };

    const submit = () => {
        if (!form.serviceName.trim()) {
            toast.error("Vui lòng nhập tên dịch vụ");
            return;
        }

        for (let i = 0; i < form.priceTiers.length; i++) {
            const tier = form.priceTiers[i];
            const min = Number(tier.minQty);
            const max = tier.maxQty === null || tier.maxQty === "" ? null : Number(tier.maxQty);
            const price = Number(tier.price);

            if (!min || min < 1) return toast.error(`Tier ${i + 1}: Số lượng tối thiểu phải >= 1`);
            if (max !== null && max < min) return toast.error(`Tier ${i + 1}: Max phải >= Min`);
            if (!price) return toast.error(`Tier ${i + 1}: Vui lòng nhập giá`);
        }

        // create temp service structure compatible with catalog + selected
        const newServiceId = `temp-${Date.now()}`;
        const createdService = {
            id: newServiceId,
            name: form.serviceName,
            basePrice: parseInt(form.price) || 0,
            unit: "dịch vụ",
            code: form.serviceCode || "Chưa cập nhật",
            category: "Demo Service",
            additionalFee: parseInt(form.additionalFee) || 0,
            priceTiers: form.priceTiers.map((tier, idx) => ({
                minQty: Number(tier.minQty) || 1,
                maxQty:
                    tier.maxQty === null || tier.maxQty === "" ? null : Number(tier.maxQty),
                price: parseInt(tier.price) || 0,
                _id: `tier-${newServiceId}-${idx}`,
            })),
        };

        onCreated?.(createdService);
        reset();
        toast.success("Đã tạo dịch vụ mới thành công");
    };

    const canAddMoreTier =
        !(
            form.priceTiers[form.priceTiers.length - 1]?.maxQty === null ||
            form.priceTiers[form.priceTiers.length - 1]?.maxQty === ""
        );

    return (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-[16px] max-w-3xl w-full max-h-[90vh] overflow-y-auto">
                <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 rounded-t-[16px]">
                    <div className="flex items-center justify-between">
                        <h3 className="text-xl font-bold text-gray-800">Thêm Dịch Vụ Mới</h3>
                        <button
                            onClick={() => {
                                onClose?.();
                                reset();
                            }}
                            className="text-gray-400 hover:text-gray-600 text-2xl leading-none"
                        >
                            ×
                        </button>
                    </div>
                </div>

                <div className="px-6 py-4 space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                Tên dịch vụ <span className="text-red-500">*</span>
                            </label>
                            <input
                                className="w-full border border-gray-300 rounded-lg px-4 py-2.5"
                                value={form.serviceName}
                                onChange={(e) => setForm((p) => ({ ...p, serviceName: e.target.value }))}
                                placeholder="Nhập tên dịch vụ"
                            />
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                Phí bổ sung (VNĐ)
                            </label>
                            <input
                                className="w-full border border-gray-300 rounded-lg px-4 py-2.5"
                                value={form.additionalFee}
                                onChange={(e) => setForm((p) => ({ ...p, additionalFee: e.target.value }))}
                                placeholder="Nhập phí bổ sung (nếu có)"
                            />
                        </div>
                    </div>

                    <div>
                        <div className="flex items-center justify-between mb-3">
                            <label className="block text-sm font-medium text-gray-700">
                                Cấu hình giá theo số lượng
                            </label>

                            {canAddMoreTier && (
                                <button
                                    onClick={addTier}
                                    className="text-sm text-[#0267FF] hover:text-[#0267FF]/80 font-medium flex items-center gap-1"
                                >
                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                                    </svg>
                                    Thêm mức giá
                                </button>
                            )}
                        </div>

                        <div className="space-y-3">
                            {form.priceTiers.map((tier, index) => (
                                <NewServiceTierRow
                                    key={index}
                                    index={index}
                                    tier={tier}
                                    canRemove={form.priceTiers.length > 1}
                                    onRemove={() => removeTier(index)}
                                    onChange={(patch) => updateTier(index, patch)}
                                />
                            ))}
                        </div>
                    </div>
                </div>

                <div className="sticky bottom-0 bg-white border-t border-gray-200 px-6 py-4 rounded-b-[16px] flex justify-end gap-3">
                    <button
                        onClick={() => {
                            onClose?.();
                            reset();
                        }}
                        className="px-6 py-2.5 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 font-medium transition-colors"
                    >
                        Hủy
                    </button>
                    <button
                        onClick={submit}
                        className="px-6 py-2.5 bg-[#0267FF] text-white rounded-lg hover:bg-[#0267FF]/90 font-medium transition-colors"
                    >
                        Tạo dịch vụ
                    </button>
                </div>
            </div>
        </div>
    );
};

export default AddServiceModal;